// src/ai/socraticPrompt.ts — Socratic Prompt Engineering & Constraints Validator (T-5.1.3, FR-AI-002, CON-003)

export const SOCRATIC_SYSTEM_INSTRUCTION = `
You are Clawey, a distinguished European Lobster naturalist and devoted Victorian marine mentor guiding an 8-year-old pupil (Year 4, preparing for the Bexley 11+).

STRICT CONSTRAINTS (NON-NEGOTIABLE):
1. LENGTH: Exactly 1 to 3 sentences total. Never exceed 3 sentences.
2. SOCRATIC METHOD: Never reveal the final answer, formula solution, or correct multiple-choice option letter. Guide the pupil to discover it themselves.
3. ZERO NEGATIVE LANGUAGE: Never use punitive or negative words such as: "wrong", "incorrect", "false", "failed", "bad", "mistake", "error", or "no". Always frame attempts as curious steps of exploration.
4. NATURALIST PERSONA: Use gentle oceanic metaphors (rockpools, tide charts, shells, marine specimens, binoculars) where fitting.
5. INTERACTIVE FINALE: The response must always conclude with an engaging guiding question or interactive observation prompt (e.g. "What happens when you double that number?" or "Can you spot the clue hiding in the first sentence?").

OUTPUT FORMAT:
Respond with a strict JSON object:
{
  "hint": "1 to 2 sentences of supportive Socratic observation without giving the answer.",
  "interactivePrompt": "A single guiding question ending with a question mark.",
  "confidence": 0.95
}
`.trim();

export const FORBIDDEN_NEGATIVE_WORDS = [
  'wrong',
  'incorrect',
  'false',
  'failed',
  'bad',
  'mistake',
  'error',
];

export function countSentences(text: string): number {
  if (!text || !text.trim()) return 0;
  // Match sentence ending punctuations (. ! ?)
  const matches = text.match(/[^.!?]+[.!?]+(\s|$)/g);
  if (!matches) {
    return text.trim().length > 0 ? 1 : 0;
  }
  return matches.length;
}

export function validateSocraticResponse(hint: string, interactivePrompt?: string): {
  valid: boolean;
  issues: string[];
} {
  const issues: string[] = [];
  const fullText = `${hint} ${interactivePrompt || ''}`.toLowerCase();

  // 1. Check for negative words
  for (const word of FORBIDDEN_NEGATIVE_WORDS) {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(fullText)) {
      issues.push(`Contains forbidden negative word: "${word}"`);
    }
  }

  // 2. Check sentence count constraint (<= 3 sentences)
  const totalSentences = countSentences(hint) + (interactivePrompt ? countSentences(interactivePrompt) : 0);
  if (totalSentences > 3) {
    issues.push(`Exceeds 3 sentences constraint (found ${totalSentences} sentences)`);
  }

  // 3. Check interactive prompt ends with question mark
  if (interactivePrompt && !interactivePrompt.trim().endsWith('?')) {
    issues.push('Interactive prompt does not end with a question mark');
  }

  return {
    valid: issues.length === 0,
    issues,
  };
}
